// @flow
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Hidden,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import SignProcess from '../model/SignProcess';
import { toSentence } from '../util/string';
import { Formik } from 'formik';
import { isNotValidEmail, isNotValidName } from '../util/validator';
import { AuthContext } from '../context/auth';

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
  },
}));

type Props = {
  code: string,
  process: SignProcess,
  expanded: boolean,
  disabled: boolean,
  onExpanded: (string) => (event: any, isExpanded: boolean) => void,
  onSave: ({
    billToFinalCustomer: boolean,
    billingName: string,
    billingEmail: string,
    billingLegalIDType: string,
    billingLegalID: string,
    billingAddress: string,
  }) => Promise<boolean>,
};

const ProcessBillingData = (props: Props) => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const { code, process, expanded, disabled, onExpanded, onSave } = props;

  const validate = values => {
    if (!!values.billToFinalCustomer) {
      return {};
    }
    const errors = {};
    if (!values.billingName) {
      errors.billingName = t('Required');
    } else if (isNotValidName(values.billingName)) {
      errors.billingName = t('Invalid name');
    }
    if (!values.billingEmail) {
      errors.billingEmail = t('Required');
    } else if (isNotValidEmail(values.billingEmail)) {
      errors.billingEmail = t('Invalid email address');
    }
    if (!values.billingLegalIDType) {
      errors.billingLegalIDType = t('Required');
    }
    if (!values.billingLegalID) {
      errors.billingLegalID = t('Required');
    }
    if (!values.billingAddress) {
      errors.billingAddress = t('Required');
    }
    return errors;
  };

  const onSubmit = async (values, { setSubmitting }) => {
    onSave(values);
    setSubmitting(false);
  };

  let processStatusText = process.hasBilling() ? 'Completed' : 'Not completed';
  let processStatusIcon = process.hasBilling() ? <DoneIcon color="action"/> : <EditIcon color="secondary"/>;
  if (process.hasBillingValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon color="primary"/>;
  }

  const userData = !!user ? user : {};

  return (
    <Formik
      initialValues={{
        sameAsProcess: process.hasSameBillingAndData(),
        billToFinalCustomer: !!process.billToFinalCustomer,
        billingName: process.billingName || userData.billingName || process.name,
        billingEmail: process.billingEmail || userData.billingEmail || process.email,
        billingLegalIDType: process.billingLegalIDType || userData.billingLegalIDType || process.legalIDType,
        billingLegalID: process.billingLegalID || userData.billingLegalID || process.legalID,
        billingAddress: process.billingAddress || userData.billingAddress || process.addressOfResidence,
      }}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
        <form className={classes.form} onSubmit={handleSubmit}>
          <Accordion
            key={code}
            expanded={expanded}
            onChange={onExpanded(code)}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
              aria-controls={`${code}-panel`}
              id={`${code}-panel`}
            >
              {processStatusIcon}&nbsp;
              <Typography className={classes.heading}>
                {t('Billing data')}
              </Typography>
              <Typography className={classes.secondaryHeading}>
                {t(processStatusText)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1}>
                {!values.billToFinalCustomer && <Grid item xs={12} sm={12} md={6} lg={4}>
                  <FormControlLabel
                    control={<Checkbox
                      disabled={disabled}
                      checked={values.sameAsProcess}
                      onChange={handleChange}
                      name="sameAsProcess"
                      color="primary"/>}
                    label={<strong>
                      {t('My personal data')}
                    </strong>}/>
                </Grid>}
                {!values.billToFinalCustomer && <Hidden smDown>
                  <Grid item xs={12} sm={12} md={6} lg={4}/>
                </Hidden>}
                {!values.billToFinalCustomer && <Hidden mdDown>
                  <Grid item xs={12} sm={12} md={6} lg={4}/>
                </Hidden>}
                {!values.sameAsProcess && <Grid item xs={12} sm={12} md={6} lg={4}>
                  <FormControlLabel
                    control={<Checkbox
                      disabled={disabled}
                      checked={values.billToFinalCustomer}
                      onChange={handleChange}
                      name="billToFinalCustomer"
                      color="primary"/>}
                    label={<strong>
                      {t('Final customer')}
                    </strong>}/>
                </Grid>}
                {(!values.sameAsProcess && !values.billToFinalCustomer) && <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled || values.billToFinalCustomer}
                    variant="outlined"
                    fullWidth
                    required
                    id="billingName"
                    label={t('Name')}
                    name="billingName"
                    autoComplete="billingName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.billingName}
                  />
                  <span className={classes.error}>
                    {errors.billingName && touched.billingName && errors.billingName}
                  </span>
                </Grid>}
                {(!values.sameAsProcess && !values.billToFinalCustomer) && <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled || values.billToFinalCustomer}
                    variant="outlined"
                    fullWidth
                    required
                    type="email"
                    id="billingEmail"
                    label={t('Email')}
                    name="billingEmail"
                    autoComplete="billingEmail"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.billingEmail}
                  />
                  <span className={classes.error}>
                    {errors.billingEmail && touched.billingEmail && errors.billingEmail}
                  </span>
                </Grid>}
                {(!values.sameAsProcess && !values.billToFinalCustomer) && <Grid item xs={12} sm={12} md={6} lg={4}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    disabled={disabled || values.billToFinalCustomer}
                  >
                    <InputLabel id="legalIDTypeLabel">
                      {t('Legal ID type')}
                    </InputLabel>
                    <Select
                      labelId="legalIDTypeLabel"
                      label={t('Legal ID type')}
                      id="billingLegalIDType"
                      name="billingLegalIDType"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.billingLegalIDType}
                    >
                      {process.validLegalIDTypes.map((item) => (
                        <MenuItem key={item} value={item}>
                          {t(toSentence(item))}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <span className={classes.error}>
                    {errors.billingLegalIDType && touched.billingLegalIDType && errors.billingLegalIDType}
                  </span>
                </Grid>}
                {(!values.sameAsProcess && !values.billToFinalCustomer) && <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled || values.billToFinalCustomer}
                    variant="outlined"
                    fullWidth
                    id="billingLegalID"
                    label={t('Legal ID')}
                    name="billingLegalID"
                    autoComplete="billingLegalID"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.billingLegalID}
                  />
                  <span className={classes.error}>
                    {errors.billingLegalID && touched.billingLegalID && errors.billingLegalID}
                  </span>
                </Grid>}
                {(!values.sameAsProcess && !values.billToFinalCustomer) && <Grid item xs={12} sm={12} md={6} lg={4}>
                  <TextField
                    disabled={disabled || values.billToFinalCustomer}
                    variant="outlined"
                    fullWidth
                    id="billingAddress"
                    label={t('Address')}
                    name="billingAddress"
                    autoComplete="billingAddress"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.billingAddress}
                  />
                  <span className={classes.error}>
                    {errors.billingAddress && touched.billingAddress && errors.billingAddress}
                  </span>
                </Grid>}
              </Grid>
            </AccordionDetails>
            {!process.hasDocumentsValidated() && <>
              <Divider/>
              <AccordionActions>
                <Button
                  type="submit"
                  disabled={disabled}
                  variant="contained"
                  color="primary"
                  startIcon={disabled ? <CircularProgress color="secondary" size="1.2em"/> : null}
                >
                  {t('Save')}
                </Button>
              </AccordionActions>
            </>}
          </Accordion>
        </form>
      )}
    </Formik>
  );
};

export default ProcessBillingData;
